import { useEffect, useMemo, useRef, useState } from "react";
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  CalendarPlus,
  ChevronLeft,
  ChevronRight,
  ListPlus,
  Plus,
  Trash2,
  UserRound,
  X,
} from "lucide-react";
import { z } from "zod";
import {
  CA_POSTAL_RE,
  ContactChannelTypeSchema,
  ContactStatusSchema,
  EmailSchema,
  PhoneE164Schema,
  PreferredCommunicationChannelSchema,
  StateOrProvinceCodeSchema,
  US_ZIP_RE,
  ZipOrPostalCodeSchema,
  stateOptionsForCountry,
  normalizeContactLanguage,
  timezonesForCountry,
  type Contact,
  type ContactChannel,
  type ContactStatus,
  type UpdateContactDto,
} from "@lumika/shared";
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  EmptyState,
  PageError,
  PageLoading,
  FormField,
  Input,
  SearchInput,
  MobileList,
  MobileListRow,
  PageHeader,
  Section,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
  Table,
  TableBleed,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Textarea,
  toast,
  useZodForm,
} from "@lumika/ui";
import { adminApi } from "../../api/admin";
import { formatDateTime, humanize } from "../../lib/format";
import {
  AppointmentFormDialog,
  NewTaskDialog,
} from "./DealershipCalendarPage";

type ContactChannelType = z.infer<typeof ContactChannelTypeSchema>;
type PreferredChannel = z.infer<typeof PreferredCommunicationChannelSchema>;

const CHANNEL_TYPE_LABELS: Record<ContactChannelType, string> = {
  phone: "Phone",
  email: "Email",
  web: "Web",
  telegram: "Telegram",
  messenger: "Messenger",
  instagram: "Instagram",
  whatsapp: "WhatsApp",
};

const PREFERRED_CHANNEL_LABELS: Record<PreferredChannel, string> = {
  voice: "Voice",
  sms: "SMS",
  email: "Email",
  web: "Web",
  messenger: "Messenger",
  instagram: "Instagram",
  whatsapp: "WhatsApp",
  telegram: "Telegram",
};

// Page-local ISO 639-1 vocabulary — the shared package has no language
// enum yet; codes are stored verbatim on Contact.preferredLanguage.
const PREFERRED_LANGUAGES = [
  { code: "en", name: "English" },
  { code: "es", name: "Spanish" },
  { code: "ru", name: "Russian" },
];

const ContactFormSchema = z
  .object({
    firstName: z.string().trim().max(80).optional(),
    lastName: z.string().trim().max(80).optional(),
    businessAccount: z.boolean(),
    businessName: z.string().trim().max(120).optional(),
    primaryEmail: z.string().email("Invalid email.").or(z.literal("")).optional(),
    primaryPhone: z.union([z.literal(""), PhoneE164Schema]).optional(),
    status: ContactStatusSchema,
    state: z.union([z.literal(""), StateOrProvinceCodeSchema]).optional(),
    city: z.string().trim().max(80).optional(),
    zip: z.union([z.literal(""), ZipOrPostalCodeSchema]).optional(),
    addressLine1: z.string().trim().max(160).optional(),
    timezone: z.string().trim().max(64).optional(),
    preferredLanguage: z.string().trim().max(35).optional(),
    preferredChannel: PreferredCommunicationChannelSchema.or(z.literal("none")),
    leadSource: z.string().trim().max(120).optional(),
    optoutEmail: z.boolean(),
    optoutSms: z.boolean(),
    optoutVoice: z.boolean(),
    notes: z.string().trim().max(4000).optional(),
    internalNotes: z.string().trim().max(4000).optional(),
  })
  .superRefine((v, ctx) => {
    if (v.businessAccount && !(v.businessName ?? "").trim())
      ctx.addIssue({
        path: ["businessName"],
        code: "custom",
        message: "Required for business accounts.",
      });
  });

type ContactFormValues = z.infer<typeof ContactFormSchema>;

// The base zip accepts either country's shape (the server stores the
// union); the dealership's country narrows the format here. The state
// dropdown only offers the country's own codes, so it needs no rule.
function contactFormSchemaForCountry(country: string) {
  const isCa = country === "CA";
  return ContactFormSchema.superRefine((values, ctx) => {
    if (values.zip && !(isCa ? CA_POSTAL_RE : US_ZIP_RE).test(values.zip)) {
      ctx.addIssue({
        code: "custom",
        path: ["zip"],
        message: isCa
          ? "Use a Canadian postal code, e.g. A1A 1A1."
          : "Use ZIP (12345) or ZIP+4 (12345-6789).",
      });
    }
  });
}

const EMPTY: ContactFormValues = {
  firstName: "",
  lastName: "",
  businessAccount: false,
  businessName: "",
  primaryEmail: "",
  primaryPhone: "",
  status: "anonymous",
  state: "",
  city: "",
  zip: "",
  addressLine1: "",
  timezone: "America/New_York",
  preferredLanguage: "en",
  preferredChannel: "none",
  leadSource: "",
  optoutEmail: false,
  optoutSms: false,
  optoutVoice: false,
  notes: "",
  internalNotes: "",
};

function toEditValues(contact: Contact): ContactFormValues {
  return {
    firstName: contact.firstName ?? "",
    lastName: contact.lastName ?? "",
    businessAccount: contact.businessAccount,
    businessName: contact.businessName ?? "",
    primaryEmail: contact.primaryEmail ?? "",
    primaryPhone: contact.primaryPhone ?? "",
    status: contact.status,
    state: contact.state ?? "",
    city: contact.city ?? "",
    zip: contact.zip ?? "",
    addressLine1: contact.addressLine1 ?? "",
    timezone: contact.timezone ?? "",
    preferredLanguage: contact.preferredLanguage ?? "",
    preferredChannel: (contact.preferredChannel as PreferredChannel | null) ?? "none",
    leadSource: contact.leadSource ?? "",
    optoutEmail: contact.optoutEmail,
    optoutSms: contact.optoutSms,
    optoutVoice: contact.optoutVoice,
    notes: contact.notes ?? "",
    internalNotes: contact.internalNotes ?? "",
  };
}

function toPayload(values: ContactFormValues): UpdateContactDto {
  const text = (value: string | undefined): string | null =>
    value && value.trim() ? value.trim() : null;
  return {
    firstName: text(values.firstName),
    lastName: text(values.lastName),
    businessAccount: values.businessAccount,
    businessName: text(values.businessName),
    primaryEmail: text(values.primaryEmail),
    primaryPhone: text(values.primaryPhone),
    status: values.status,
    state: text(values.state),
    city: text(values.city),
    zip: text(values.zip),
    addressLine1: text(values.addressLine1),
    timezone: text(values.timezone),
    preferredLanguage: normalizeContactLanguage(values.preferredLanguage),
    preferredChannel: values.preferredChannel === "none" ? null : values.preferredChannel,
    leadSource: text(values.leadSource),
    optoutEmail: values.optoutEmail,
    optoutSms: values.optoutSms,
    optoutVoice: values.optoutVoice,
    notes: text(values.notes),
    internalNotes: text(values.internalNotes),
  };
}

function contactDisplayName(contact: Contact): string {
  return contact.displayName ?? contact.fallbackLabel ?? "Anonymous";
}

const PAGE_SIZE = 25;

export function DealershipContactsPage() {
  const { dealershipId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [editing, setEditing] = useState<Contact | "new" | null>(null);
  const [taskForContact, setTaskForContact] = useState<Contact | null>(null);
  const [apptForContact, setApptForContact] = useState<Contact | null>(null);
  const [search, setSearch] = useState("");
  // Debounce the free-text search 300ms so each keystroke doesn't fire a
  // request; the debounced value drives the server query.
  const [debouncedSearch, setDebouncedSearch] = useState(search);
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(t);
  }, [search]);
  const trimmedSearch = debouncedSearch.trim();
  // 1-based page in `?page=`; the backend already caps each page at
  // PAGE_SIZE, so without this control only the first page was reachable.
  const page = Math.max(1, Number(searchParams.get("page") ?? "1") || 1);

  const detailQuery = useQuery({
    queryKey: ["dealership", dealershipId],
    queryFn: () => adminApi.getDealership(dealershipId!),
    enabled: Boolean(dealershipId),
  });
  const contactsQuery = useQuery({
    queryKey: ["admin-bdc", dealershipId, "contacts", trimmedSearch, page],
    queryFn: () => adminApi.listContacts(dealershipId!, trimmedSearch, page, PAGE_SIZE),
    enabled: Boolean(dealershipId),
    // Keep the previous rows visible while a new search/page loads
    // instead of blanking the table into a spinner.
    placeholderData: keepPreviousData,
  });

  const contacts = contactsQuery.data?.items ?? [];
  const total = contactsQuery.data?.total ?? 0;
  const rooftops = detailQuery.data?.rooftops ?? [];
  const users = detailQuery.data?.users ?? [];

  function goToPage(nextPage: number) {
    const next = new URLSearchParams(searchParams);
    if (nextPage <= 1) next.delete("page");
    else next.set("page", String(nextPage));
    setSearchParams(next, { replace: true });
  }

  // A new search changes the result set, so a stale `?page=N` could point
  // past the end — reset to page 1 whenever the search term changes.
  const lastSearchRef = useRef(trimmedSearch);
  useEffect(() => {
    if (lastSearchRef.current === trimmedSearch) return;
    lastSearchRef.current = trimmedSearch;
    if (page !== 1) goToPage(1);
  }, [trimmedSearch]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const rangeStart = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const rangeEnd = Math.min(page * PAGE_SIZE, total);

  // A stale `?page=N` past the last page returns 0 rows while total > 0 —
  // clamp back to the last real page instead of rendering the empty state.
  useEffect(() => {
    if (contactsQuery.isLoading || contactsQuery.isFetching) return;
    if (contacts.length === 0 && total > 0 && page > 1) goToPage(totalPages);
  }, [contactsQuery.isLoading, contactsQuery.isFetching, contacts.length, total, page, totalPages]);

  // Deep link from the Contact 360 page's Edit button:
  // /contacts?edit=:id pops the existing form dialog so admins can still
  // mutate the row without duplicating the form. Fetched by id so the
  // target doesn't need to be on the currently loaded page.
  const editParam = searchParams.get("edit");
  const editContactQuery = useQuery({
    queryKey: ["admin-bdc", dealershipId, "contact", editParam],
    queryFn: () => adminApi.getContact(dealershipId!, editParam!),
    enabled: Boolean(dealershipId && editParam),
  });
  useEffect(() => {
    if (editParam && editContactQuery.data) setEditing(editContactQuery.data);
  }, [editParam, editContactQuery.data]);
  useEffect(() => {
    if (!editParam || !editContactQuery.isError) return;
    toast.error("Couldn't load that contact to edit.");
    const next = new URLSearchParams(searchParams);
    next.delete("edit");
    setSearchParams(next, { replace: true });
  }, [editParam, editContactQuery.isError]);

  return (
    <div className="flex flex-col gap-6 p-6">
      <PageHeader
        eyebrow="Customers"
        title="Contacts"
        description="People and businesses tracked across every channel. Click a row to open the full Customer 360 view."
        actions={
          <Button onClick={() => setEditing("new")}>
            <Plus className="h-4 w-4" />
            New contact
          </Button>
        }
      />

      <Section
        title={contactsQuery.data ? `${total} contacts` : "Contacts"}
        actions={
          <SearchInput
            containerClassName="w-64"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name, email, phone"
            aria-label="Search contacts"
          />
        }
      >
        {contactsQuery.isLoading ? (
          <PageLoading message="Loading contacts…" />
        ) : contactsQuery.isError ? (
          <PageError
            title="Couldn't load contacts"
            description="Try refreshing the page."
          />
        ) : contacts.length === 0 ? (
          <EmptyState
            icon={<UserRound className="h-5 w-5" />}
            title={trimmedSearch ? "No contacts match" : "No contacts yet"}
            description={
              trimmedSearch
                ? "Try a different search term or clear the filter."
                : "Create one manually or wait for the first inbound channel message."
            }
            action={
              !trimmedSearch && (
                <Button onClick={() => setEditing("new")}>
                  <Plus className="h-4 w-4" />
                  New contact
                </Button>
              )
            }
          />
        ) : (
          <>
            <TableBleed className="-mt-6 hidden border-b md:block">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Phone</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Last activity</TableHead>
                    <TableHead className="w-32 text-right">Add</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {contacts.map((contact) => (
                    <TableRow
                      key={contact.id}
                      className="cursor-pointer"
                      onClick={() =>
                        navigate(`/dealerships/${dealershipId}/contacts/${contact.id}`)
                      }
                    >
                      <TableCell className="font-medium">
                        {/* Real link so keyboard users and cmd-click work;
                            the row onClick stays as a mouse convenience. */}
                        <Link
                          to={`/dealerships/${dealershipId}/contacts/${contact.id}`}
                          className="hover:underline"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {contactDisplayName(contact)}
                        </Link>
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {contact.primaryEmail ?? "—"}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {contact.primaryPhone ?? "—"}
                      </TableCell>
                      <TableCell>
                        <Badge variant={contact.status === "anonymous" ? "secondary" : "outline"}>
                          {contact.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {formatDateTime(contact.lastActivityAt)}
                      </TableCell>
                      <TableCell
                        className="text-right"
                        // Buttons live inside a row whose own onClick opens the
                        // contact editor; stop propagation so the row doesn't
                        // also fire when the operator clicks an action icon.
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div className="inline-flex gap-1">
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            aria-label="New task for this contact"
                            title="New task"
                            onClick={() => setTaskForContact(contact)}
                          >
                            <ListPlus className="h-4 w-4" />
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            aria-label="Book appointment for this contact"
                            title="Book appointment"
                            onClick={() => setApptForContact(contact)}
                          >
                            <CalendarPlus className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableBleed>
            <MobileList>
              {contacts.map((contact) => (
                <MobileListRow
                  key={contact.id}
                  onClick={() =>
                    navigate(`/dealerships/${dealershipId}/contacts/${contact.id}`)
                  }
                  primary={contactDisplayName(contact)}
                  secondary={contact.primaryEmail ?? undefined}
                  tertiary={contact.primaryPhone ?? undefined}
                  meta={
                    <>
                      <Badge variant={contact.status === "anonymous" ? "secondary" : "outline"}>
                        {contact.status}
                      </Badge>
                      <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
                        {formatDateTime(contact.lastActivityAt)}
                      </span>
                    </>
                  }
                  trailing={
                    // Same row actions as the table; stop propagation so a tap
                    // on an action icon doesn't also fire the row navigation.
                    <div
                      className="inline-flex gap-1"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        aria-label="New task for this contact"
                        title="New task"
                        onClick={() => setTaskForContact(contact)}
                      >
                        <ListPlus className="h-4 w-4" />
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        aria-label="Book appointment for this contact"
                        title="Book appointment"
                        onClick={() => setApptForContact(contact)}
                      >
                        <CalendarPlus className="h-4 w-4" />
                      </Button>
                    </div>
                  }
                />
              ))}
            </MobileList>
            {/* Server-paged — controls move the window across the whole
                result set, not just a client slice. */}
            <div className="mt-3 flex items-center justify-between gap-3 text-sm text-muted-foreground">
              <span>
                {rangeStart}–{rangeEnd} of {total}
              </span>
              <div className="flex items-center gap-2">
                <span className="hidden sm:inline">
                  Page {page} of {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => goToPage(page - 1)}
                  disabled={page <= 1}
                >
                  <ChevronLeft className="h-4 w-4" />
                  Prev
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => goToPage(page + 1)}
                  disabled={page >= totalPages}
                >
                  Next
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </>
        )}
      </Section>

      {taskForContact && dealershipId && (
        <NewTaskDialog
          dealershipId={dealershipId}
          contacts={contacts}
          rooftops={rooftops}
          users={users}
          presetContactId={taskForContact.id}
          onClose={() => setTaskForContact(null)}
        />
      )}
      {apptForContact && dealershipId && (
        <AppointmentFormDialog
          mode="create"
          appointment={null}
          presetStart={null}
          contacts={[apptForContact]}
          rooftops={rooftops}
          users={users}
          dealershipId={dealershipId}
          onClose={() => setApptForContact(null)}
        />
      )}

      {editing !== null && dealershipId && (
        <ContactFormDialog
          mode={editing === "new" ? "create" : "edit"}
          contact={editing === "new" ? null : editing}
          dealershipId={dealershipId}
          onClose={() => {
            setEditing(null);
            if (searchParams.has("edit")) {
              const next = new URLSearchParams(searchParams);
              next.delete("edit");
              setSearchParams(next, { replace: true });
            }
          }}
        />
      )}
    </div>
  );
}

function ContactFormDialog({
  mode,
  contact,
  dealershipId,
  onClose,
}: {
  mode: "create" | "edit";
  contact: Contact | null;
  dealershipId: string;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const dealershipQ = useQuery({
    queryKey: ["dealership", dealershipId],
    queryFn: () => adminApi.getDealership(dealershipId),
  });
  const country = dealershipQ.data?.country ?? "US";
  const isCa = country === "CA";
  const formSchema = useMemo(() => contactFormSchemaForCountry(country), [country]);
  const form = useZodForm(formSchema, contact ? toEditValues(contact) : EMPTY);

  const createMutation = useMutation({
    mutationFn: (input: ContactFormValues) =>
      adminApi.createContact(
        dealershipId,
        toPayload(input) as Parameters<typeof adminApi.createContact>[1],
      ),
    onSuccess: async () => {
      await invalidate(qc, dealershipId);
      toast.success("Contact created");
      onClose();
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const updateMutation = useMutation({
    mutationFn: (input: ContactFormValues) =>
      adminApi.updateContact(dealershipId, contact!.id, toPayload(input)),
    onSuccess: async () => {
      await invalidate(qc, dealershipId);
      toast.success("Contact updated");
    },
    onError: (error: Error) => toast.error(error.message),
  });

  function submit() {
    const result = form.validate();
    if (!result.ok) return;
    if (mode === "create") createMutation.mutate(result.data);
    else updateMutation.mutate(result.data);
  }

  const { values, errors, setField } = form;
  const isPending = createMutation.isPending || updateMutation.isPending;
  const isBusiness = values.businessAccount;
  // Once the admin has typed anything, block the implicit close paths
  // (outside click, Escape) so a stray click can't wipe the form.
  const isDirty =
    JSON.stringify(values) !== JSON.stringify(contact ? toEditValues(contact) : EMPTY);

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl" preventDismiss={isDirty}>
        <DialogHeader>
          <DialogTitle>
            {mode === "create"
              ? "New contact"
              : contact
                ? `Edit ${contactDisplayName(contact)}`
                : "Edit contact"}
          </DialogTitle>
          <DialogDescription>
            Profile, address, channels, and preferences for this person or business.
          </DialogDescription>
        </DialogHeader>

        <div className="grid max-h-[60vh] gap-5 overflow-y-auto -mx-1 px-1">
          <FieldGroup title="Identity">
            <FormField
              className="sm:col-span-2"
              label="Business account"
              description="Toggle on for fleet, dealer-to-dealer, or commercial customers."
            >
              <div className="flex items-center gap-3">
                <Switch
                  checked={values.businessAccount}
                  onCheckedChange={(checked) => setField("businessAccount", checked)}
                />
                <span className="text-sm text-muted-foreground">
                  {isBusiness ? "Business" : "Individual"}
                </span>
              </div>
            </FormField>
            {isBusiness && (
              <FormField
                className="sm:col-span-2"
                label="Business name"
                required={isBusiness}
                error={errors.businessName}
              >
                <Input
                  value={values.businessName ?? ""}
                  onChange={(e) => setField("businessName", e.target.value)}
                  placeholder="Acme Logistics"
                />
              </FormField>
            )}
            <FormField label="First name" optional error={errors.firstName}>
              <Input
                value={values.firstName ?? ""}
                onChange={(e) => setField("firstName", e.target.value)}
                autoFocus={mode === "create"}
              />
            </FormField>
            <FormField label="Last name" optional error={errors.lastName}>
              <Input
                value={values.lastName ?? ""}
                onChange={(e) => setField("lastName", e.target.value)}
              />
            </FormField>
            <FormField label="Primary email" optional error={errors.primaryEmail}>
              <Input
                type="email"
                value={values.primaryEmail ?? ""}
                onChange={(e) => setField("primaryEmail", e.target.value)}
              />
            </FormField>
            <FormField label="Primary phone" optional error={errors.primaryPhone}>
              <Input
                type="tel"
                value={values.primaryPhone ?? ""}
                onChange={(e) => setField("primaryPhone", e.target.value)}
                placeholder="+15551234567"
              />
            </FormField>
            <FormField label="Status" error={errors.status}>
              <Select
                value={values.status}
                onValueChange={(v) => setField("status", v as ContactStatus)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ContactStatusSchema.options.map((option) => (
                    <SelectItem key={option} value={option}>
                      {humanize(option)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FormField>
          </FieldGroup>

          <FieldGroup title="Address">
            <FormField className="sm:col-span-2" label="Street" optional error={errors.addressLine1}>
              <Input
                value={values.addressLine1 ?? ""}
                onChange={(e) => setField("addressLine1", e.target.value)}
              />
            </FormField>
            <FormField label="City" optional error={errors.city}>
              <Input
                value={values.city ?? ""}
                onChange={(e) => setField("city", e.target.value)}
              />
            </FormField>
            <FormField label={isCa ? "Province" : "State"} optional error={errors.state}>
              <Select
                value={values.state === "" ? "__none__" : (values.state ?? "__none__")}
                onValueChange={(v) =>
                  setField("state", v === "__none__" ? "" : v)
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder={isCa ? "Pick a province" : "Pick a state"} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— None —</SelectItem>
                  {/* Imported contacts can carry the other country's code —
                      keep it selectable so the trigger isn't blank. */}
                  {values.state &&
                    !stateOptionsForCountry(country).some((s) => s.code === values.state) && (
                      <SelectItem value={values.state}>{values.state}</SelectItem>
                    )}
                  {stateOptionsForCountry(country).map((s) => (
                    <SelectItem key={s.code} value={s.code}>
                      {s.code} — {s.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FormField>
            <FormField label={isCa ? "Postal code" : "ZIP"} optional error={errors.zip}>
              <Input
                value={values.zip ?? ""}
                onChange={(e) => setField("zip", e.target.value)}
                placeholder={isCa ? "A1A 1A1" : "12345 or 12345-6789"}
              />
            </FormField>
          </FieldGroup>

          <FieldGroup title="Preferences">
            <FormField label="Timezone" optional error={errors.timezone}>
              <Select
                value={values.timezone === "" ? "__none__" : (values.timezone ?? "__none__")}
                onValueChange={(v) => setField("timezone", v === "__none__" ? "" : v)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Pick a timezone" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— None —</SelectItem>
                  {/* Imported contacts can carry any IANA zone — keep the
                      current value selectable so the trigger isn't blank. */}
                  {values.timezone &&
                    !timezonesForCountry(country).includes(values.timezone) && (
                      <SelectItem value={values.timezone}>{values.timezone}</SelectItem>
                    )}
                  {timezonesForCountry(country).map((tz) => (
                    <SelectItem key={tz} value={tz}>
                      {tz}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FormField>
            <FormField label="Preferred language" optional error={errors.preferredLanguage}>
              <Select
                value={
                  values.preferredLanguage === ""
                    ? "__none__"
                    : (values.preferredLanguage ?? "__none__")
                }
                onValueChange={(v) => setField("preferredLanguage", v === "__none__" ? "" : v)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Pick a language" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— None —</SelectItem>
                  {/* Imported contacts can carry any language tag — keep the
                      current value selectable so the trigger isn't blank. */}
                  {values.preferredLanguage &&
                    !PREFERRED_LANGUAGES.some((l) => l.code === values.preferredLanguage) && (
                      <SelectItem value={values.preferredLanguage}>
                        {values.preferredLanguage}
                      </SelectItem>
                    )}
                  {PREFERRED_LANGUAGES.map((l) => (
                    <SelectItem key={l.code} value={l.code}>
                      {l.code} — {l.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FormField>
            <FormField className="sm:col-span-2" label="Preferred channel" error={errors.preferredChannel}>
              <Select
                value={values.preferredChannel}
                onValueChange={(v) =>
                  setField("preferredChannel", v as ContactFormValues["preferredChannel"])
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No preference</SelectItem>
                  {PreferredCommunicationChannelSchema.options.map((option) => (
                    <SelectItem key={option} value={option}>
                      {PREFERRED_CHANNEL_LABELS[option]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FormField>
          </FieldGroup>

          {mode === "edit" && contact && (
            <ContactChannelsEditor
              dealershipId={dealershipId}
              contact={contact}
            />
          )}

          <FieldGroup title="Marketing & consent">
            <FormField label="Email opt-out">
              <Switch
                checked={values.optoutEmail}
                onCheckedChange={(checked) => setField("optoutEmail", checked)}
              />
            </FormField>
            <FormField label="SMS opt-out">
              <Switch
                checked={values.optoutSms}
                onCheckedChange={(checked) => setField("optoutSms", checked)}
              />
            </FormField>
            <FormField label="Voice opt-out">
              <Switch
                checked={values.optoutVoice}
                onCheckedChange={(checked) => setField("optoutVoice", checked)}
              />
            </FormField>
            <FormField label="Lead source" optional error={errors.leadSource}>
              <Input
                value={values.leadSource ?? ""}
                onChange={(e) => setField("leadSource", e.target.value)}
                placeholder="cars.com / dealer.com / walk-in"
              />
            </FormField>
          </FieldGroup>

          <FieldGroup title="Notes">
            <FormField className="sm:col-span-2" label="Notes" optional error={errors.notes}>
              <Textarea
                value={values.notes ?? ""}
                onChange={(e) => setField("notes", e.target.value)}
                rows={3}
                placeholder="Visible to all operators."
              />
            </FormField>
            <FormField
              className="sm:col-span-2"
              label="Internal notes"
              optional
              error={errors.internalNotes}
            >
              <Textarea
                value={values.internalNotes ?? ""}
                onChange={(e) => setField("internalNotes", e.target.value)}
                rows={3}
                placeholder="lumika-only, hidden from dealership operators."
              />
            </FormField>
          </FieldGroup>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            {mode === "edit" ? "Close" : "Cancel"}
          </Button>
          <Button onClick={submit} disabled={isPending}>
            {isPending
              ? "Saving…"
              : mode === "create"
                ? "Create contact"
                : "Save contact"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const ChannelDraftSchema = z
  .object({
    type: ContactChannelTypeSchema,
    externalId: z.string().trim().min(1, "Required.").max(160),
    value: z.string().trim().max(160).optional(),
    label: z.string().trim().max(80).optional(),
    isPrimary: z.boolean(),
  })
  // Mirrors refineContactChannelValue in @lumika/shared's bdc.dto.ts
  // (not exported): both the lookup key and the display value carry the
  // channel's address, so shape-check each present one against the type.
  .superRefine((v, ctx) => {
    for (const field of ["externalId", "value"] as const) {
      const raw = v[field];
      if (!raw) continue;
      if (v.type === "email" && !EmailSchema.safeParse(raw).success)
        ctx.addIssue({
          path: [field],
          code: "custom",
          message: "Enter a valid email address.",
        });
      if (
        (v.type === "phone" || v.type === "whatsapp") &&
        !PhoneE164Schema.safeParse(raw).success
      )
        ctx.addIssue({
          path: [field],
          code: "custom",
          message: "Use E.164 format, e.g. +15551234567.",
        });
    }
  });

type ChannelDraft = z.infer<typeof ChannelDraftSchema>;

function ContactChannelsEditor({
  dealershipId,
  contact,
}: {
  dealershipId: string;
  contact: Contact;
}) {
  const qc = useQueryClient();
  const [adding, setAdding] = useState(false);
  const draftForm = useZodForm(ChannelDraftSchema, {
    type: "email",
    externalId: "",
    value: "",
    label: "",
    isPrimary: false,
  });

  const channelsQuery = useQuery({
    queryKey: ["admin-bdc", dealershipId, "contact-channels", contact.id],
    queryFn: () => adminApi.listContactChannels(dealershipId, contact.id),
  });

  const channels = useMemo(() => channelsQuery.data?.items ?? [], [channelsQuery.data]);

  const createMutation = useMutation({
    mutationFn: (input: ChannelDraft) =>
      adminApi.createContactChannel(dealershipId, {
        contactId: contact.id,
        type: input.type,
        externalId: input.externalId.trim(),
        value: input.value?.trim() ? input.value.trim() : null,
        label: input.label?.trim() ? input.label.trim() : null,
        isPrimary: input.isPrimary,
        isMarketingOptedIn: false,
      }),
    onSuccess: async () => {
      setAdding(false);
      draftForm.reset();
      await invalidateChannels(qc, dealershipId, contact.id);
      toast.success("Channel added");
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const togglePrimary = useMutation({
    mutationFn: ({ id, isPrimary }: { id: string; isPrimary: boolean }) =>
      adminApi.updateContactChannel(dealershipId, id, { isPrimary }),
    onSuccess: () => invalidateChannels(qc, dealershipId, contact.id),
    onError: (error: Error) => toast.error(error.message),
  });

  const toggleOptIn = useMutation({
    mutationFn: ({ id, isMarketingOptedIn }: { id: string; isMarketingOptedIn: boolean }) =>
      adminApi.updateContactChannel(dealershipId, id, { isMarketingOptedIn }),
    onSuccess: () => invalidateChannels(qc, dealershipId, contact.id),
    onError: (error: Error) => toast.error(error.message),
  });

  const removeMutation = useMutation({
    mutationFn: (id: string) => adminApi.removeContactChannel(dealershipId, id),
    onSuccess: async () => {
      await invalidateChannels(qc, dealershipId, contact.id);
      toast.success("Channel removed");
    },
    onError: (error: Error) => toast.error(error.message),
  });

  function addChannel() {
    const result = draftForm.validate();
    if (!result.ok) return;
    createMutation.mutate(result.data);
  }

  return (
    <fieldset className="grid gap-3">
      <legend className="flex w-full items-center justify-between text-xs font-bold uppercase tracking-[0.12em] text-muted-foreground">
        Channels
        {!adding && (
          <Button size="sm" variant="outline" onClick={() => setAdding(true)}>
            <Plus className="h-3 w-3" />
            Add channel
          </Button>
        )}
      </legend>

      {channels.length === 0 && !adding && (
        <div className="rounded-md border border-dashed bg-muted/30 p-4 text-center text-sm text-muted-foreground">
          No channels yet. Phone, email, and social handles attach the contact to specific lead surfaces.
        </div>
      )}

      {channels.map((channel: ContactChannel) => (
        <div
          key={channel.id}
          className="grid gap-3 rounded-md border bg-card p-3 sm:grid-cols-[1fr_1fr_auto]"
        >
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <Badge variant="secondary">{CHANNEL_TYPE_LABELS[channel.type]}</Badge>
              <span className="truncate font-mono text-sm">{channel.externalId}</span>
            </div>
            {(channel.value || channel.label) && (
              <div className="mt-1 text-xs text-muted-foreground">
                {[channel.label, channel.value].filter(Boolean).join(" · ")}
              </div>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-4">
            <label className="flex cursor-pointer items-center gap-2 text-sm">
              <Switch
                checked={channel.isPrimary}
                onCheckedChange={(checked) =>
                  togglePrimary.mutate({ id: channel.id, isPrimary: checked })
                }
                disabled={togglePrimary.isPending}
              />
              <span>Primary</span>
            </label>
            <label className="flex cursor-pointer items-center gap-2 text-sm">
              <Switch
                checked={channel.isMarketingOptedIn}
                onCheckedChange={(checked) =>
                  toggleOptIn.mutate({ id: channel.id, isMarketingOptedIn: checked })
                }
                disabled={toggleOptIn.isPending}
              />
              <span>Marketing opt-in</span>
            </label>
          </div>
          <Button
            variant="ghost"
            size="icon"
            aria-label="Remove channel"
            onClick={() => {
              if (
                confirm(
                  `Remove ${CHANNEL_TYPE_LABELS[channel.type]} channel ${channel.externalId}? This cannot be undone.`,
                )
              ) {
                removeMutation.mutate(channel.id);
              }
            }}
            disabled={removeMutation.isPending}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      ))}

      {adding && (
        <div className="grid gap-3 rounded-md border bg-card p-3">
          <div className="grid gap-3 sm:grid-cols-2">
            <FormField label="Type" required>
              <Select
                value={draftForm.values.type}
                onValueChange={(v) =>
                  draftForm.setField("type", v as ContactChannelType)
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ContactChannelTypeSchema.options.map((option) => (
                    <SelectItem key={option} value={option}>
                      {CHANNEL_TYPE_LABELS[option]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FormField>
            <FormField
              label="External ID"
              required
              error={draftForm.errors.externalId}
              description="Provider lookup key: email address, E.164 phone, social handle."
            >
              <Input
                value={draftForm.values.externalId}
                onChange={(e) => draftForm.setField("externalId", e.target.value)}
                autoFocus
              />
            </FormField>
            <FormField label="Display value" optional error={draftForm.errors.value}>
              <Input
                value={draftForm.values.value ?? ""}
                onChange={(e) => draftForm.setField("value", e.target.value)}
              />
            </FormField>
            <FormField label="Label" optional error={draftForm.errors.label}>
              <Input
                value={draftForm.values.label ?? ""}
                onChange={(e) => draftForm.setField("label", e.target.value)}
                placeholder="work / personal"
              />
            </FormField>
          </div>
          <label className="flex cursor-pointer items-center gap-3 text-sm">
            <Switch
              checked={draftForm.values.isPrimary}
              onCheckedChange={(checked) => draftForm.setField("isPrimary", checked)}
            />
            <span>Mark as primary for this type</span>
          </label>
          <div className="flex items-center justify-end gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setAdding(false);
                draftForm.reset();
              }}
            >
              <X className="h-4 w-4" />
              Cancel
            </Button>
            <Button size="sm" onClick={addChannel} disabled={createMutation.isPending}>
              {createMutation.isPending ? "Adding…" : "Add channel"}
            </Button>
          </div>
        </div>
      )}
    </fieldset>
  );
}

function FieldGroup({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <fieldset className="grid gap-3">
      <legend className="text-xs font-bold uppercase tracking-[0.12em] text-muted-foreground">
        {title}
      </legend>
      <div className="grid gap-4 sm:grid-cols-2">{children}</div>
    </fieldset>
  );
}

async function invalidate(qc: ReturnType<typeof useQueryClient>, dealershipId?: string) {
  if (!dealershipId) return;
  await qc.invalidateQueries({ queryKey: ["admin-bdc", dealershipId, "contacts"] });
}

async function invalidateChannels(
  qc: ReturnType<typeof useQueryClient>,
  dealershipId: string,
  contactId: string,
) {
  await Promise.all([
    qc.invalidateQueries({
      queryKey: ["admin-bdc", dealershipId, "contact-channels", contactId],
    }),
    qc.invalidateQueries({ queryKey: ["admin-bdc", dealershipId, "contacts"] }),
  ]);
}
